import { useCallback, useEffect } from 'react';

import { useTracking } from '@change-corgi/core/react/tracking';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';

import { updateEndorsement } from '../../api';
import { getErrorType, getPhotoUrl } from '../../shared/helpers';
import type { EndorsementError } from '../../shared/hooks';
import { useEndorsementForm } from '../../shared/hooks';
import type { EndorsementFormData } from '../../shared/types';
import { useEndorsementImageUpload } from '../useEndorsementImageUpload';

export type { EndorsementError } from '../../shared/hooks';

type UseEditEndorsementProps = {
	endorsementId: string;
	initialData?: Partial<EndorsementFormData>;
	onSuccess?: () => void;
};

type TrackFn = (event: string, data?: Record<string, unknown>) => Promise<boolean>;
type SetErrorFn = (error: EndorsementError | null) => void;

function handleSubmitError(track: TrackFn, setError: SetErrorFn, msg: string, type: EndorsementError['type']) {
	void track('starter_dashboard_endorsement_update_error', { error: msg });
	setError({ message: msg, type });
}

function buildUpdateInput(endorsementId: string, petitionId: string, formData: EndorsementFormData, photoUrl?: string) {
	return {
		endorsementId,
		petitionId,
		name: formData.name || undefined,
		title: formData.title || undefined,
		url: formData.url || undefined,
		content: formData.statement || undefined,
		...(photoUrl && { photo: { url: photoUrl } }),
	};
}

// Sync image from upload context to form data
function useSyncImageToForm(image: string | undefined, handlePhotoUpload: (url: string) => void) {
	useEffect(() => {
		if (image) handlePhotoUpload(image);
	}, [image, handlePhotoUpload]);
}

// eslint-disable-next-line max-lines-per-function
export function useEditEndorsement({ endorsementId, initialData, onSuccess }: UseEditEndorsementProps) {
	const track = useTracking();
	const utilityContext = useUtilityContext();
	const {
		data: { petition },
	} = useStarterDashboardV2PageData();
	const form = useEndorsementForm(initialData);
	const imageUpload = useEndorsementImageUpload();

	useSyncImageToForm(imageUpload.data.image, form.handlePhotoUpload);

	const handleSubmit = useCallback(async () => {
		form.setIsSubmitting(true);
		form.setError(null);
		try {
			void track('starter_dashboard_endorsement_edit_submit_click');
			const uploadResult = await getPhotoUrl(imageUpload.data.image, imageUpload.uploader);
			if (uploadResult && !uploadResult.success) {
				handleSubmitError(track, form.setError, uploadResult.error, 'SERVER_ERROR');
				return;
			}
			const input = buildUpdateInput(endorsementId, petition.id, form.formData, uploadResult?.url);
			const response = await updateEndorsement(input, utilityContext);
			if (response.success) {
				void track('starter_dashboard_endorsement_update_success');
				onSuccess?.();
			} else {
				const msg = response.errorMessage || 'Failed to update endorsement';
				handleSubmitError(track, form.setError, msg, getErrorType(msg));
			}
		} catch (e) {
			handleSubmitError(track, form.setError, e instanceof Error ? e.message : 'Unexpected error', 'NETWORK_ERROR');
		} finally {
			form.setIsSubmitting(false);
		}
	}, [
		endorsementId,
		petition.id,
		form,
		utilityContext,
		track,
		onSuccess,
		imageUpload.data.image,
		imageUpload.uploader,
	]);

	return {
		data: {
			formData: form.formData,
			isSubmitting: form.isSubmitting,
			statementCharCount: form.statementCharCount,
			statementMaxLength: form.statementMaxLength,
			error: form.error,
			image: imageUpload.data.image,
		},
		actions: {
			handleFieldChange: form.handleFieldChange,
			handlePhotoUpload: form.handlePhotoUpload,
			handleSubmit,
			clearError: form.clearError,
			resetForm: form.resetForm,
		},
		uploader: imageUpload.uploader,
	};
}
